import { hasQuarterStatsSource, type LeagueKey } from './league';
/**
 * Market key types shared between frontend and backend.
 * Single source of truth for valid Odds API market identifiers.
 */

// =============================================================================
// Player Prop Markets
// =============================================================================

/** Base player prop market identifiers (standard Over/Under format) */
export type BasePlayerPropMarket =
  | 'player_points'
  | 'player_rebounds'
  | 'player_assists'
  | 'player_threes'
  | 'player_blocks'
  | 'player_steals'
  | 'player_turnovers'
  | 'player_field_goals'
  | 'player_frees_made'
  | 'player_blocks_steals'
  | 'player_points_rebounds_assists'
  | 'player_points_rebounds'
  | 'player_points_assists'
  | 'player_rebounds_assists'
  | 'player_double_double'
  | 'player_triple_double'
  | 'player_first_basket'
  | 'player_first_team_basket';

/** Alternate player prop markets (X+ format — exact line = win) */
export type AlternatePlayerPropMarket =
  | 'player_points_alternate'
  | 'player_rebounds_alternate'
  | 'player_assists_alternate'
  | 'player_threes_alternate'
  | 'player_steals_alternate'
  | 'player_blocks_alternate'
  | 'player_turnovers_alternate'
  | 'player_field_goals_alternate'
  | 'player_blocks_steals_alternate'
  | 'player_points_rebounds_assists_alternate'
  | 'player_points_rebounds_alternate'
  | 'player_points_assists_alternate'
  | 'player_rebounds_assists_alternate';

/** Q1 player prop markets (first quarter stats) */
export type Q1PlayerPropMarket = 'player_points_q1' | 'player_rebounds_q1' | 'player_assists_q1';

/** All player prop markets combined */
export type PlayerPropMarket =
  | BasePlayerPropMarket
  | AlternatePlayerPropMarket
  | Q1PlayerPropMarket;

// =============================================================================
// Game-Level Markets
// =============================================================================

/** Core game-level market identifiers */
export type CoreGameMarket = 'h2h' | 'spreads' | 'totals';

/** Team total markets */
export type TeamTotalMarket =
  | 'team_totals'
  | 'team_totals_h1'
  | 'team_totals_h2'
  | 'team_totals_q1'
  | 'team_totals_q2'
  | 'team_totals_q3'
  | 'team_totals_q4';

/** Alternate game-level markets */
export type AlternateGameMarket =
  | 'alternate_spreads'
  | 'alternate_totals'
  | 'alternate_spreads_h1'
  | 'alternate_spreads_h2'
  | 'alternate_totals_h1'
  | 'alternate_totals_h2'
  | 'alternate_team_totals'
  | 'alternate_team_totals_h1'
  | 'alternate_team_totals_h2';

/** Half game markets */
export type HalfGameMarket =
  | 'h2h_h1'
  | 'h2h_h2'
  | 'spreads_h1'
  | 'spreads_h2'
  | 'totals_h1'
  | 'totals_h2';

/** Quarter game markets */
export type QuarterGameMarket =
  | 'h2h_q1'
  | 'h2h_q2'
  | 'h2h_q3'
  | 'h2h_q4'
  | 'spreads_q1'
  | 'spreads_q2'
  | 'spreads_q3'
  | 'spreads_q4'
  | 'totals_q1'
  | 'totals_q2'
  | 'totals_q3'
  | 'totals_q4';

/** All game-level markets combined */
export type GameMarket =
  | CoreGameMarket
  | TeamTotalMarket
  | AlternateGameMarket
  | HalfGameMarket
  | QuarterGameMarket;

// =============================================================================
// Combined Market Type
// =============================================================================

/** All valid market types */
export type Market = PlayerPropMarket | GameMarket;

// =============================================================================
// Const Arrays (for iteration/validation)
// =============================================================================

/** Base player prop markets */
export const BASE_PLAYER_PROP_MARKETS: readonly BasePlayerPropMarket[] = [
  'player_points',
  'player_rebounds',
  'player_assists',
  'player_threes',
  'player_blocks',
  'player_steals',
  'player_turnovers',
  'player_field_goals',
  'player_frees_made',
  'player_blocks_steals',
  'player_points_rebounds_assists',
  'player_points_rebounds',
  'player_points_assists',
  'player_rebounds_assists',
  'player_double_double',
  'player_triple_double',
  'player_first_basket',
  'player_first_team_basket',
] as const;

/** Alternate player prop markets */
export const ALTERNATE_PLAYER_PROP_MARKETS: readonly AlternatePlayerPropMarket[] = [
  'player_points_alternate',
  'player_rebounds_alternate',
  'player_assists_alternate',
  'player_threes_alternate',
  'player_steals_alternate',
  'player_blocks_alternate',
  'player_turnovers_alternate',
  'player_field_goals_alternate',
  'player_blocks_steals_alternate',
  'player_points_rebounds_assists_alternate',
  'player_points_rebounds_alternate',
  'player_points_assists_alternate',
  'player_rebounds_assists_alternate',
] as const;

/**
 * Is this an ALTERNATE ("X+" / milestone) market? Player alts use a `_alternate` SUFFIX
 * (player_points_alternate); game alts use an `alternate_` PREFIX (alternate_spreads,
 * alternate_team_totals); some feeds label milestone markets with `milestone`. Alt lines are a
 * DIFFERENT bet than the main line ("X+" = actual >= X, one-sided, heavily juiced) and must never
 * be treated as a standard over/under: settlement wins-at-exact for alt overs, and generation keeps
 * them out of the main single/anchor pool (learned 2026-08-02).
 *
 * Single source of truth — settlement (`settlementHelperUtils`) imports this; do not re-define.
 */
export function isAlternateMarket(marketKey: string | null | undefined): boolean {
  if (!marketKey) return false;
  return (
    marketKey.endsWith('_alternate') ||
    marketKey.startsWith('alternate_') ||
    marketKey.includes('milestone')
  );
}

/**
 * One-sided expected-value fraction for an alternate "X+" leg: `modelProb * decimalOdds - 1`.
 * Positive = +EV, negative = the juice outweighs the probability (e.g. a -1000 near-lock needs
 * modelProb > ~0.909 to clear break-even). Used to keep heavily-juiced alts from reading as value,
 * since alts are one-sided (no under price) so the two-sided no-vig edge can't apply.
 * Returns null on missing/invalid input (never throws).
 */
export function altEvPct(
  modelProb: number | null | undefined,
  americanOdds: number | null | undefined,
): number | null {
  if (modelProb == null || americanOdds == null) return null;
  if (!Number.isFinite(modelProb) || !Number.isFinite(americanOdds)) return null;
  if (modelProb < 0 || modelProb > 1 || americanOdds === 0) return null;
  const decimal = americanOdds > 0 ? 1 + americanOdds / 100 : 1 + 100 / Math.abs(americanOdds);
  return modelProb * decimal - 1;
}

/** Q1 player prop markets */
export const Q1_PLAYER_PROP_MARKETS: readonly Q1PlayerPropMarket[] = [
  'player_points_q1',
  'player_rebounds_q1',
  'player_assists_q1',
] as const;

/** All player prop markets combined */
export const PLAYER_PROP_MARKETS: readonly PlayerPropMarket[] = [
  ...BASE_PLAYER_PROP_MARKETS,
  ...ALTERNATE_PLAYER_PROP_MARKETS,
  ...Q1_PLAYER_PROP_MARKETS,
] as const;

/** Core game markets */
export const CORE_GAME_MARKETS: readonly CoreGameMarket[] = ['h2h', 'spreads', 'totals'] as const;

/** Team total markets */
export const TEAM_TOTAL_MARKETS: readonly TeamTotalMarket[] = [
  'team_totals',
  'team_totals_h1',
  'team_totals_h2',
  'team_totals_q1',
  'team_totals_q2',
  'team_totals_q3',
  'team_totals_q4',
] as const;

/** Alternate game markets */
export const ALTERNATE_GAME_MARKETS: readonly AlternateGameMarket[] = [
  'alternate_spreads',
  'alternate_totals',
  'alternate_spreads_h1',
  'alternate_spreads_h2',
  'alternate_totals_h1',
  'alternate_totals_h2',
  'alternate_team_totals',
  'alternate_team_totals_h1',
  'alternate_team_totals_h2',
] as const;

/** Half game markets */
export const HALF_GAME_MARKETS: readonly HalfGameMarket[] = [
  'h2h_h1',
  'h2h_h2',
  'spreads_h1',
  'spreads_h2',
  'totals_h1',
  'totals_h2',
] as const;

/** Quarter game markets */
export const QUARTER_GAME_MARKETS: readonly QuarterGameMarket[] = [
  'h2h_q1',
  'h2h_q2',
  'h2h_q3',
  'h2h_q4',
  'spreads_q1',
  'spreads_q2',
  'spreads_q3',
  'spreads_q4',
  'totals_q1',
  'totals_q2',
  'totals_q3',
  'totals_q4',
] as const;

/** All game markets combined */
export const GAME_MARKETS: readonly GameMarket[] = [
  ...CORE_GAME_MARKETS,
  ...TEAM_TOTAL_MARKETS,
  ...ALTERNATE_GAME_MARKETS,
  ...HALF_GAME_MARKETS,
  ...QUARTER_GAME_MARKETS,
] as const;

/** All markets combined */
export const ALL_MARKETS: readonly Market[] = [...PLAYER_PROP_MARKETS, ...GAME_MARKETS];

// =============================================================================
// Validators
// =============================================================================

/** Check if a string is a valid Market */
export function isValidMarket(value: unknown): value is Market {
  return typeof value === 'string' && (ALL_MARKETS as readonly string[]).includes(value);
}

/** Check if a market is a player prop (vs game-level) */
export function isPlayerPropMarket(market: string): market is PlayerPropMarket {
  return (PLAYER_PROP_MARKETS as readonly string[]).includes(market);
}

/** Check if a market is a game market */
export function isGameMarket(market: string): market is GameMarket {
  return (GAME_MARKETS as readonly string[]).includes(market);
}

/** Check if a market is a team total */
export function isTeamTotalMarket(market: string): market is TeamTotalMarket {
  return (TEAM_TOTAL_MARKETS as readonly string[]).includes(market);
}

/** Check if a market is an alternate game market */
export function isAlternateGameMarket(market: string): market is AlternateGameMarket {
  return (ALTERNATE_GAME_MARKETS as readonly string[]).includes(market);
}

// =============================================================================
// Period Helpers
// =============================================================================

export type Period = 'h1' | 'h2' | 'q1' | 'q2' | 'q3' | 'q4';

export interface PeriodInfo {
  base: string;
  period: Period;
  isAlternate: boolean;
  isTeamTotal: boolean;
}

/**
 * Check if a market key represents a period-specific market (half or quarter).
 * Matches patterns like: totals_h1, spreads_q3, team_totals_h2, alternate_totals_q1
 */
export function isPeriodMarket(market: string): boolean {
  return /_(h[12]|q[1-4])$/.test(market);
}

/**
 * Parse a market key into its components.
 * Examples:
 *   'totals_h1' → { base: 'totals', period: 'h1', isAlternate: false, isTeamTotal: false }
 *   'team_totals_q2' → { base: 'team_totals', period: 'q2', isAlternate: false, isTeamTotal: true }
 *   'alternate_spreads_h1' → { base: 'alternate_spreads', period: 'h1', isAlternate: true, isTeamTotal: false }
 *   'player_points' → null (not a period market)
 */
export function getPeriodInfo(market: string): PeriodInfo | null {
  const periodMatch = market.match(/^(.+)_(h[12]|q[1-4])$/);
  if (!periodMatch) return null;

  const base = periodMatch[1];
  const period = periodMatch[2] as Period;

  return {
    base,
    period,
    isAlternate: base.startsWith('alternate_'),
    isTeamTotal: base.includes('team_total'),
  };
}

/** A market key without its alternate prefix or period suffix: `alternate_spreads_h1` reads as `spreads`. */
export function marketFamily(marketKey: string): string {
  return marketKey.replace(/^alternate_/, '').replace(/_(h1|h2|q1|q2|q3|q4)$/, '');
}

/** Spreads of any period, alternates included; their snapshots hold the home team's line and price. */
export function isSpreadMarket(marketKey: string | null | undefined): boolean {
  return !!marketKey && marketFamily(marketKey) === 'spreads';
}

/** A moneyline or spread of any period, alternates included: the bet backs a team and has no Over/Under. */
export function isTeamSideMarket(marketKey: string | null | undefined): boolean {
  if (!marketKey) return false;
  const family = marketFamily(marketKey);
  return family === 'h2h' || family === 'spreads';
}

/** Singular/internal period base → canonical Odds-API base. */
const PERIOD_BASE_TO_CANONICAL: Record<string, string> = {
  total: 'totals',
  spread: 'spreads',
  moneyline: 'h2h',
  team_total: 'team_totals',
  alternate_team_total: 'alternate_team_totals',
};

/**
 * Canonicalize a period market key (or period statType) to its Odds-API form.
 * Examples:
 *   'total_h1'       → 'totals_h1'
 *   'moneyline_h2'   → 'h2h_h2'
 *   'spread_q2'      → 'spreads_q2'
 *   'team_total_h1'  → 'team_totals_h1'
 * Already-canonical keys ('totals_h1'), alternate markets, player props
 * ('player_points_q1'), and non-period keys pass through unchanged.
 */
export function canonicalizePeriodMarketKey(key: string): string {
  const info = getPeriodInfo(key);
  if (!info) return key;
  const canonicalBase = PERIOD_BASE_TO_CANONICAL[info.base] ?? info.base;
  return `${canonicalBase}_${info.period}`;
}

/**
 * A whole-game total (Over/Under on both teams' points), the one game market with no bet team.
 * Accepts market keys and statTypes, including alternate and period forms; team totals are not.
 */
export function isGameTotalMarket(key: string | null | undefined): boolean {
  if (!key) return false;
  const base = (getPeriodInfo(key)?.base ?? key).toLowerCase().replace(/^alternate_/, '');
  return base === 'totals' || base === 'total';
}

// =============================================================================
// MarketOption (for UI display — shared between web and mobile)
// =============================================================================

export interface MarketOption {
  key: string;
  label: string;
  category: string;
}

/** All available markets for the MarketSelector UI */
export const AVAILABLE_MARKETS: MarketOption[] = [
  // Game markets
  { key: 'h2h', label: 'Moneyline', category: 'game' },
  { key: 'spreads', label: 'Spreads', category: 'game' },
  { key: 'totals', label: 'Game Totals', category: 'game' },
  { key: 'alternate_spreads', label: 'Alt Spreads', category: 'game' },
  { key: 'alternate_totals', label: 'Alt Totals', category: 'game' },
  // Team markets
  { key: 'team_totals', label: 'Team Total', category: 'team' },
  { key: 'alternate_team_totals', label: 'Alt Team Total', category: 'team' },
  // Basic player props
  { key: 'player_points', label: 'Points', category: 'basic' },
  { key: 'player_rebounds', label: 'Rebounds', category: 'basic' },
  { key: 'player_assists', label: 'Assists', category: 'basic' },
  { key: 'player_threes', label: '3-Pointers', category: 'basic' },
  { key: 'player_blocks', label: 'Blocks', category: 'basic' },
  { key: 'player_steals', label: 'Steals', category: 'basic' },
  { key: 'player_turnovers', label: 'Turnovers', category: 'basic' },
  // Shooting props
  { key: 'player_field_goals', label: 'Field Goals', category: 'shooting' },
  { key: 'player_frees_made', label: 'Free Throws', category: 'shooting' },
  // Combo props
  { key: 'player_blocks_steals', label: 'BLK+STL', category: 'combo' },
  { key: 'player_points_rebounds_assists', label: 'PTS+REB+AST', category: 'combo' },
  { key: 'player_points_rebounds', label: 'PTS+REB', category: 'combo' },
  { key: 'player_points_assists', label: 'PTS+AST', category: 'combo' },
  { key: 'player_rebounds_assists', label: 'REB+AST', category: 'combo' },
  // Special props
  { key: 'player_double_double', label: 'Double-Double', category: 'special' },
  { key: 'player_triple_double', label: 'Triple-Double', category: 'special' },
  { key: 'player_first_basket', label: 'First Basket', category: 'special' },
  { key: 'player_first_team_basket', label: 'First Team Basket', category: 'special' },
  // Alternate player props
  { key: 'player_points_alternate', label: 'Alt Points', category: 'alt_player' },
  { key: 'player_rebounds_alternate', label: 'Alt Rebounds', category: 'alt_player' },
  { key: 'player_assists_alternate', label: 'Alt Assists', category: 'alt_player' },
  { key: 'player_threes_alternate', label: 'Alt 3-Pointers', category: 'alt_player' },
  { key: 'player_steals_alternate', label: 'Alt Steals', category: 'alt_player' },
  { key: 'player_blocks_alternate', label: 'Alt Blocks', category: 'alt_player' },
  { key: 'player_turnovers_alternate', label: 'Alt Turnovers', category: 'alt_player' },
  { key: 'player_field_goals_alternate', label: 'Alt Field Goals', category: 'alt_player' },
  { key: 'player_blocks_steals_alternate', label: 'Alt BLK+STL', category: 'alt_player' },
  {
    key: 'player_points_rebounds_assists_alternate',
    label: 'Alt PTS+REB+AST',
    category: 'alt_player',
  },
  { key: 'player_points_rebounds_alternate', label: 'Alt PTS+REB', category: 'alt_player' },
  { key: 'player_points_assists_alternate', label: 'Alt PTS+AST', category: 'alt_player' },
  { key: 'player_rebounds_assists_alternate', label: 'Alt REB+AST', category: 'alt_player' },
  // Half — Game
  { key: 'h2h_h1', label: '1H Moneyline', category: 'half_game' },
  { key: 'h2h_h2', label: '2H Moneyline', category: 'half_game' },
  { key: 'spreads_h1', label: '1H Spread', category: 'half_game' },
  { key: 'spreads_h2', label: '2H Spread', category: 'half_game' },
  { key: 'totals_h1', label: '1H Total', category: 'half_game' },
  { key: 'totals_h2', label: '2H Total', category: 'half_game' },
  { key: 'alternate_spreads_h1', label: '1H Alt Spread', category: 'half_game' },
  { key: 'alternate_spreads_h2', label: '2H Alt Spread', category: 'half_game' },
  { key: 'alternate_totals_h1', label: '1H Alt Total', category: 'half_game' },
  { key: 'alternate_totals_h2', label: '2H Alt Total', category: 'half_game' },
  // Half — Team
  { key: 'team_totals_h1', label: '1H Team Total', category: 'half_team' },
  { key: 'team_totals_h2', label: '2H Team Total', category: 'half_team' },
  { key: 'alternate_team_totals_h1', label: '1H Alt Team Total', category: 'half_team' },
  { key: 'alternate_team_totals_h2', label: '2H Alt Team Total', category: 'half_team' },
  // Quarter — Game
  { key: 'h2h_q1', label: 'Q1 Moneyline', category: 'quarter_game' },
  { key: 'h2h_q2', label: 'Q2 Moneyline', category: 'quarter_game' },
  { key: 'h2h_q3', label: 'Q3 Moneyline', category: 'quarter_game' },
  { key: 'h2h_q4', label: 'Q4 Moneyline', category: 'quarter_game' },
  { key: 'spreads_q1', label: 'Q1 Spread', category: 'quarter_game' },
  { key: 'spreads_q2', label: 'Q2 Spread', category: 'quarter_game' },
  { key: 'spreads_q3', label: 'Q3 Spread', category: 'quarter_game' },
  { key: 'spreads_q4', label: 'Q4 Spread', category: 'quarter_game' },
  { key: 'totals_q1', label: 'Q1 Total', category: 'quarter_game' },
  { key: 'totals_q2', label: 'Q2 Total', category: 'quarter_game' },
  { key: 'totals_q3', label: 'Q3 Total', category: 'quarter_game' },
  { key: 'totals_q4', label: 'Q4 Total', category: 'quarter_game' },
  // Quarter — Team
  { key: 'team_totals_q1', label: 'Q1 Team Total', category: 'quarter_team' },
  { key: 'team_totals_q2', label: 'Q2 Team Total', category: 'quarter_team' },
  { key: 'team_totals_q3', label: 'Q3 Team Total', category: 'quarter_team' },
  { key: 'team_totals_q4', label: 'Q4 Team Total', category: 'quarter_team' },
  // Quarter — Player
  { key: 'player_points_q1', label: 'Q1 Points', category: 'quarter_player' },
  { key: 'player_rebounds_q1', label: 'Q1 Rebounds', category: 'quarter_player' },
  { key: 'player_assists_q1', label: 'Q1 Assists', category: 'quarter_player' },
];

// =============================================================================
// League-Specific Market Filtering
// =============================================================================

/**
 * NCAAB game-level markets only (20 confirmed via live Odds API).
 * No player props, no quarter markets (NCAAB plays halves).
 * h2h_h2 is NOT available for NCAAB.
 */
export const NCAAB_MARKET_KEYS: ReadonlySet<string> = new Set([
  // Core game markets
  'h2h',
  'spreads',
  'totals',
  'alternate_spreads',
  'alternate_totals',
  // Team markets
  'team_totals',
  'alternate_team_totals',
  // 1st half markets
  'h2h_h1',
  'spreads_h1',
  'totals_h1',
  'team_totals_h1',
  'alternate_spreads_h1',
  'alternate_totals_h1',
  'alternate_team_totals_h1',
  // 2nd half markets (h2h_h2 NOT available for NCAAB)
  'spreads_h2',
  'totals_h2',
  'team_totals_h2',
  'alternate_spreads_h2',
  'alternate_totals_h2',
  'alternate_team_totals_h2',
]);

/** Game markets to auto-select when switching to a league without player props and none of its markets enabled */
export const GAME_MARKET_DEFAULT_KEYS = ['h2h', 'spreads', 'totals'] as const;

/** EuroLeague markets: game-level only, no player props, no period markets. */
export const EUROLEAGUE_GAME_MARKETS = ['h2h', 'spreads', 'totals'] as const;

/** Leagues whose markets are a subset of AVAILABLE_MARKETS; any other league offers them all. */
const LEAGUE_MARKET_KEYS: Partial<Record<LeagueKey, ReadonlySet<string>>> = {
  basketball_ncaab: NCAAB_MARKET_KEYS,
  basketball_wncaab: NCAAB_MARKET_KEYS,
  basketball_euroleague: new Set<string>(EUROLEAGUE_GAME_MARKETS),
};

/** A player prop on one period (player_points_q1) is offered only where a quarter source settles it. */
export function offersMarket(leagueKey: LeagueKey, market: string): boolean {
  return (
    !(market.startsWith('player_') && isPeriodMarket(market)) || hasQuarterStatsSource(leagueKey)
  );
}

/** The markets a league is offered, in the order given. */
export function offeredMarkets(leagueKey: LeagueKey, markets: readonly string[]): string[] {
  return markets.filter((market) => offersMarket(leagueKey, market));
}

/** Filter AVAILABLE_MARKETS to only those valid for the given league (accepts LeagueKey) */
export function getAvailableMarketsForLeague(leagueKey: LeagueKey): MarketOption[] {
  const keys = LEAGUE_MARKET_KEYS[leagueKey];
  return AVAILABLE_MARKETS.filter(
    (m) => (!keys || keys.has(m.key)) && offersMarket(leagueKey, m.key),
  );
}

// =============================================================================
// Other Shared Types
// =============================================================================

/** Over/Under direction for player props */
export type OverUnder = 'over' | 'under';

/** Parlay origin — where the legs came from */
export type ParlayOrigin = 'ai' | 'manual' | 'bet';
